import React from 'react';
import { useHistory } from 'react-router-dom';
import { Pagination } from '@gerdau/hefesto-mais-web';
import { SortProductsItem } from '../../services/MVP/models';
import { usePricingService } from '../../services/MVP/usePricingService';
import ProductCard from '../../components/Purchase/ProductCard';
import { EPurchaseRoutes } from '../Purchase/purchase.routes';
import { EOrderSettingsRoutes } from '../Purchase/LongSteel/OrderSettingsMvp/orderSettings.routes';
import favoriteIconEmpty from '../../assets/images/favorite_empty.svg';
import {
  EmptyStateContainer,
  EmptyStateText,
  EmptyStateLink,
  FavoritesGrid,
  PaginationContainer
} from './styles';
import { MaterialCode } from './useFavorites';

const VITRINE_PATH = `/purchase/${EPurchaseRoutes.LONG_STEEL}/${EPurchaseRoutes.COMMERCE}/${EOrderSettingsRoutes.CATALOG}`;

const getProductImage = (prd: SortProductsItem) => {
  if (prd?.media_gallery_entries?.[0]?.file) {
    return {
      desktopSource: `${process.env.REACT_APP_MEDIA_BASE_URL}/media/catalog/product/${prd.media_gallery_entries[0].file}`
    };
  }
  if (
    prd?.extension_attributes?.category_links &&
    Array.isArray(prd.extension_attributes.category_links) &&
    prd.extension_attributes.category_links.length > 0 &&
    prd.extension_attributes.category_links[0]?.category_image
  ) {
    return {
      desktopSource: `${process.env.REACT_APP_MEDIA_BASE_URL}${prd.extension_attributes.category_links[0].category_image}`
    };
  }
  return undefined;
};

interface FavoritesContentProps {
  hasEmitterAndReceiver: boolean;
  loading: boolean;
  submittedSearch: string;
  displayedProducts: SortProductsItem[];
  allFavorites: SortProductsItem[];
  total: number;
  currentPage: number;
  materialCode: MaterialCode;
  emitterId: string;
  receiverId: string;
  pageSize: number;
  handleRemoveFavorite: (sku: string) => void;
  handlePageChange: (page: number, pageSize: number) => void;
}

const FavoritesContent: React.FC<FavoritesContentProps> = ({
  hasEmitterAndReceiver,
  loading,
  submittedSearch,
  displayedProducts,
  allFavorites,
  total,
  currentPage,
  materialCode,
  emitterId,
  receiverId,
  pageSize,
  handleRemoveFavorite,
  handlePageChange
}) => {
  const history = useHistory();
  const { getPrice } = usePricingService();

  if (!hasEmitterAndReceiver) {
    return (
      <EmptyStateContainer>
        <EmptyStateText>
          Selecione emissor e recebedor para acessar sua lista de favoritos
        </EmptyStateText>
      </EmptyStateContainer>
    );
  }

  if (loading) {
    return null;
  }

  if (submittedSearch && displayedProducts.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateText>
          Nenhum resultado encontrado para a busca por{' '}
          <strong>&quot;{submittedSearch}&quot;</strong>.
          <br />
          Tente novamente com uma descrição ou código de material diferentes.
        </EmptyStateText>
      </EmptyStateContainer>
    );
  }

  if (allFavorites.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateText>
          Ainda não há nenhum produto na sua lista de favoritos.
          <br />
          Para favoritar, clique no <strong>ícone de coração</strong>{' '}
          <img src={favoriteIconEmpty} alt="" /> nos produtos na{' '}
          <EmptyStateLink onClick={() => history.push(VITRINE_PATH)}>
            vitrine
          </EmptyStateLink>{' '}
          ou carrinho
        </EmptyStateText>
      </EmptyStateContainer>
    );
  }

  return (
    <>
      <FavoritesGrid>
        {displayedProducts.map((prd) => {
          if (!prd.id) return null;
          const price = getPrice(prd);
          const agreementPrice = price?.isAgreementPrice?.length !== 0;
          return (
            <ProductCard
              key={prd.sku}
              agreedPrice={agreementPrice}
              image={getProductImage(prd)}
              showLink={false}
              product={prd}
              typeCode={materialCode}
              onRemoveFavorite={handleRemoveFavorite}
              taggingContext={{
                screenName: 'pagina_favoritos',
                tipoCompra: 'favoritos'
              }}
            />
          );
        })}
      </FavoritesGrid>
      <PaginationContainer disableNextPage={total <= pageSize}>
        <Pagination
          key={`${emitterId}-${receiverId}`}
          ariaLabel="pagination-favoritos"
          onChange={(page: number, pageTotal: number) => {
            const isPageSizeChange = pageTotal !== pageSize;
            if (!isPageSizeChange && total <= pageSize) return;
            handlePageChange(Math.ceil(page), pageTotal);
          }}
          total={total}
          showTotal
          selectedPage={currentPage}
          selectedPageSizeOption={pageSize}
          pageSizeOptions={[12, 24]}
        />
      </PaginationContainer>
    </>
  );
};

export default FavoritesContent;
